import { BreakpointKey, WidgetSize, WidgetSizeConfig, WidgetSizeVariant, WidgetSizeVariantConfig } from '../types';

// ============================================================================
// Breakpoint Configuration
// ============================================================================

export interface BreakpointConfig {
  /** Breakpoint identifier */
  name: BreakpointKey;
  /** Minimum viewport width (inclusive) */
  minWidth: number;
  /** Maximum viewport width (exclusive) */
  maxWidth?: number;
  /** Total grid columns (always 12) */
  columns: number;
  /** Base height of one grid row in pixels */
  rowHeight: number;
  /** Gap between grid items in pixels */
  gap: number;
}

export const BREAKPOINT_CONFIGS: BreakpointConfig[] = [
  { name: 'base', minWidth: 0, maxWidth: 576, columns: 12, rowHeight: 32, gap: 12 },
  { name: 'sm', minWidth: 576, maxWidth: 768, columns: 12, rowHeight: 36, gap: 12 },
  { name: 'md', minWidth: 768, maxWidth: 992, columns: 12, rowHeight: 40, gap: 14 },
  { name: 'lg', minWidth: 992, maxWidth: 1200, columns: 12, rowHeight: 44, gap: 16 },
  { name: 'xl', minWidth: 1200, maxWidth: 1920, columns: 12, rowHeight: 48, gap: 16 },
  { name: 'tv', minWidth: 1920, columns: 12, rowHeight: 56, gap: 20 },
];

// ============================================================================
// Breakpoint Utilities
// ============================================================================

/**
 * Get the current breakpoint configuration based on viewport width
 */
export function getCurrentBreakpoint(width: number): BreakpointConfig {
  // Iterate from largest to smallest to find matching breakpoint
  for (let i = BREAKPOINT_CONFIGS.length - 1; i >= 0; i--) {
    if (width >= BREAKPOINT_CONFIGS[i].minWidth) {
      return BREAKPOINT_CONFIGS[i];
    }
  }
  return BREAKPOINT_CONFIGS[0]; // Default to base
}

/**
 * Get breakpoint config by name
 */
export function getBreakpointConfig(name: BreakpointKey): BreakpointConfig {
  const config = BREAKPOINT_CONFIGS.find((bp) => bp.name === name);
  return config || BREAKPOINT_CONFIGS[0];
}

/**
 * Get widget size for a specific breakpoint, with fallback to smaller breakpoints
 */
export function getWidgetSizeForBreakpoint(
  sizes: WidgetSizeConfig,
  breakpoint: BreakpointKey
): WidgetSize {
  // Priority order for fallback
  const fallbackOrder: BreakpointKey[] = ['tv', 'xl', 'lg', 'md', 'sm', 'base'];
  const breakpointIndex = fallbackOrder.indexOf(breakpoint);

  // Try the exact breakpoint first, then fall back to smaller breakpoints
  for (let i = breakpointIndex; i < fallbackOrder.length; i++) {
    const bp = fallbackOrder[i];
    if (sizes[bp]) {
      return sizes[bp]!;
    }
  }

  // Always fall back to base (which is required)
  return sizes.base;
}

/**
 * Calculate total height needed for a widget at a given breakpoint
 */
export function calculateWidgetHeight(
  rows: number,
  breakpoint: BreakpointKey
): number {
  const config = getBreakpointConfig(breakpoint);
  return rows * config.rowHeight + (rows - 1) * config.gap;
}

/**
 * Check if viewport is considered mobile
 */
export function isMobileBreakpoint(breakpoint: BreakpointKey): boolean {
  return breakpoint === 'base' || breakpoint === 'sm';
}

/**
 * Check if viewport is considered tablet
 */
export function isTabletBreakpoint(breakpoint: BreakpointKey): boolean {
  return breakpoint === 'md';
}

/**
 * Check if viewport is considered desktop
 */
export function isDesktopBreakpoint(breakpoint: BreakpointKey): boolean {
  return breakpoint === 'lg' || breakpoint === 'xl';
}

/**
 * Check if viewport is TV/large display
 */
export function isTvBreakpoint(breakpoint: BreakpointKey): boolean {
  return breakpoint === 'tv';
}

/**
 * Get CSS media query for a breakpoint
 */
export function getMediaQuery(breakpoint: BreakpointKey): string {
  const config = getBreakpointConfig(breakpoint);
  if (config.maxWidth) {
    return `(min-width: ${config.minWidth}px) and (max-width: ${config.maxWidth - 1}px)`;
  }
  return `(min-width: ${config.minWidth}px)`;
}

// ============================================================================
// Grid Constants
// ============================================================================

export const GRID_COLUMNS = 12;
export const MIN_WIDGET_COLS = 3; // Minimum 3 columns (25% on desktop)
export const MIN_WIDGET_ROWS = 2; // Minimum 2 rows

// ============================================================================
// Size Variant Utilities
// ============================================================================

/**
 * Get widget size for a specific breakpoint with size variant support.
 * Falls back to default sizes if variant is not found.
 * Supports separate phone/tablet/desktop variants.
 *
 * Device hierarchy:
 * - Phone (base, sm): < 768px - typically 6 or 12 cols for readable content
 * - Tablet (md): 768px - 992px - typically 4-6 cols
 * - Desktop (lg, xl, tv): > 992px - full flexibility
 */
export function getWidgetSizeWithVariant(
  defaultSizes: WidgetSizeConfig,
  sizeVariants: WidgetSizeVariantConfig | undefined,
  selectedVariant: WidgetSizeVariant | undefined,
  breakpoint: BreakpointKey,
  phoneSizeVariant?: WidgetSizeVariant,
  tabletSizeVariant?: WidgetSizeVariant
): WidgetSize {
  // Determine which variant to use based on breakpoint
  // Fallback chain: phone -> tablet -> desktop
  let effectiveVariant = selectedVariant;

  if (isMobileBreakpoint(breakpoint)) {
    // Phone: use phoneSizeVariant, fallback to tabletSizeVariant, then desktop
    effectiveVariant = phoneSizeVariant || tabletSizeVariant || selectedVariant;
  } else if (isTabletBreakpoint(breakpoint)) {
    // Tablet: use tabletSizeVariant, fallback to desktop
    effectiveVariant = tabletSizeVariant || selectedVariant;
  }
  // Desktop (lg, xl, tv): use selectedVariant as-is

  // If no variants or no selected variant, use default sizes
  if (!sizeVariants || !effectiveVariant) {
    return getWidgetSizeForBreakpoint(defaultSizes, breakpoint);
  }

  // Get the variant's size configuration
  const variantSizes = sizeVariants.variantSizes[effectiveVariant];
  if (!variantSizes) {
    return getWidgetSizeForBreakpoint(defaultSizes, breakpoint);
  }

  return getWidgetSizeForBreakpoint(variantSizes, breakpoint);
}

/**
 * Get display text for a size (e.g., "6x4" for 6 columns, 4 rows)
 */
export function formatSizeDisplay(size: WidgetSize): string {
  return `${size.cols} × ${size.rows}`;
}

/**
 * Get human-readable size description
 */
export function getSizeDescription(size: WidgetSize): string {
  const widthPercent = Math.round((size.cols / GRID_COLUMNS) * 100);
  let widthLabel: string;

  if (widthPercent <= 25) widthLabel = 'Quarter';
  else if (widthPercent <= 50) widthLabel = 'Half';
  else if (widthPercent <= 75) widthLabel = 'Three-quarters';
  else widthLabel = 'Full';

  let heightLabel: string;
  if (size.rows <= 2) heightLabel = 'compact';
  else if (size.rows <= 4) heightLabel = 'standard';
  else if (size.rows <= 6) heightLabel = 'tall';
  else heightLabel = 'extra tall';

  return `${widthLabel} width, ${heightLabel} height`;
}
